import { i18next } from '@ingenico/react-i18n';
import { ShellRoot } from '@ingenico/react-mfe-shell-utils';
import { getAuthService } from '@ingenico/util-mfe-shell';
import { useEffect, useState, type ReactElement } from 'react';
import type { DataRouter } from 'react-router';
import { filter, take } from 'rxjs';
import { appContainer } from '../../hexa/appContainer.const';
import { saveReturnUrlCookie } from '../../utils/browser.util';

interface StandaloneShellProps {
  readonly appInitPromise: Promise<DataRouter>;
}

export function StandaloneShell({ appInitPromise }: StandaloneShellProps): ReactElement {
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const authService = getAuthService(appContainer);
    if (!authService) {
      return;
    }

    let reloadFallback: number | undefined;
    const subscription = authService
      .getState()
      .pipe(
        filter(
          ({ keepAliveStoppedDueToInactivityOrExpiredSession, keepAliveStoppedForce }) =>
            keepAliveStoppedDueToInactivityOrExpiredSession || keepAliveStoppedForce,
        ),
        take(1),
      )
      .subscribe(() => {
        setSessionExpired(true);
        saveReturnUrlCookie();

        reloadFallback = window.setTimeout(() => window.location.reload(), 3_000);
        authService.deleteSession().subscribe({
          error: () => window.location.reload(),
        });
      });

    return () => {
      subscription.unsubscribe();
      if (reloadFallback !== undefined) {
        window.clearTimeout(reloadFallback);
      }
    };
  }, []);

  if (sessionExpired) {
    return null;
  }

  return (
    <ShellRoot appInitPromise={appInitPromise} getAuthServiceFn={() => getAuthService(appContainer)} i18n={i18next} />
  );
}
